import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, DropdownList, SearchField, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ValueSource} from './valueSources/valueSource.types';

const tokens = tokensForThemeProvider.sky.sys;

export interface ConditionValuePickerProps {
	source: ValueSource;
	selectedIds: string[];
	onChange: (ids: string[]) => void;
	/** Rendered above the search field. Null (or omitted) when the field offers no operator choice.
	 * A truthy element renders a divider beneath it, so a caller with no operator choice to offer
	 * must pass a literal `null` here rather than an element that itself renders `null` (that would
	 * still be truthy and render a divider with nothing above it). */
	operatorPicker?: JSX.Element | null;
}

/** `DropdownList.Content` pads its content vertically only. The operator control and the search
 * field are bare design-system inputs, so they need this horizontal padding or they render flush
 * against the dropdown's edges. The value rows deliberately DO NOT get it: they have to span the
 * full width so the hover highlights the whole row, and `DropdownList.CheckboxItem` supplies its
 * own 16px padding through `selectableItemCss`.
 *
 * A plain `div`, not `styled(FlexColumn)`: `FlexColumn` destructures `data-cy` out of its props
 * and never forwards it to the underlying element, so a `data-cy` passed to it is silently
 * dropped and never reaches the DOM. */
const InsetGroup = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0 ${tokens.spacing['400']};
`;

/** Same padding as `InsetGroup`, plus the vertical gap between the error message and the retry
 * button — expressed as a CSS `gap` rather than `FlexColumn`'s `gap` prop, since a plain `div`
 * takes no such prop. 4px, matching the design system's smallest named gap. */
const InsetGroupWithGap = styled(InsetGroup)`
	gap: ${tokens.spacing['100']};
`;

/** The design system exports no divider component. This 1px rule, combined with the surrounding
 * gap, reproduces the confirmed 16px divider slot between sections. */
const Divider = styled.div`
	height: 1px;
	flex: none;
	background: ${tokens.color.border.decorative};
`;

/** The Select all divider sits inside `RowList`, which is `gap: 0` so the value rows below it stay
 * contiguous. That means it is the one divider with no surrounding gap to borrow spacing from, and
 * without this it renders flush against both neighbours while the two above it sit in a 16px slot.
 * The margin restores that slot — 8px either side of the rule — so all three read the same. */
const SectionDivider = styled(Divider)`
	margin: ${tokens.spacing['200']} 0;
`;

/** Caps the picker to the space the dropdown actually has, so it can never be positioned partly
 * off-screen. Radix clamps content that fits neither above nor below the trigger half out of the
 * viewport rather than shrinking it, and publishes the room it measured as
 * `--radix-dropdown-menu-content-available-height` on the content element for exactly this case.
 *
 * Capped at 490px as well as by that variable, because `DropdownList.Content` carries its own fixed
 * `max-height: 490px` with `overflow-y: auto`. Take only the variable and, wherever more than ~506px
 * is available, this element grows taller than the content that holds it — so the CONTENT scrolls
 * instead, carrying the operator control and search field out of view with the list. The pinned
 * header only holds while this element stays within the content's own cap.
 *
 * The 18px is that element's chrome, measured rather than assumed: 8px padding and a 1px border top
 * and bottom, against `box-sizing: border-box`.
 *
 * `min-height: 0` is required: a flex child defaults to `min-height: auto`, which refuses to
 * shrink below its content and would defeat the cap. */
const PickerShell = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.spacing['200']};
	min-height: 0;
	max-height: calc(min(var(--radix-dropdown-menu-content-available-height, 490px), 490px) - 18px);
`;

/** The value rows must render contiguous, not gapped: the confirmed row pitch is exactly the
 * 44px row height, with nothing between rows. `FlexColumn` always applies a gap (falling back
 * to a default when none is given), so this sets it to `0` explicitly rather than relying on
 * that fallback — a gap here would also break the full-row hover reading as one continuous
 * list. */
const RowList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0;

	/* Takes whatever vertical space the shell's cap leaves after the operator control, the search
	 * field and their dividers, and scrolls within it. Letting flexbox do that subtraction is what
	 * keeps this free of a hand-tuned header offset that would drift as the header changes.
	 * min-height 0 for the same reason as on the shell: a flex child will not shrink below its
	 * content height without it. */
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
`;

/** Search + checkbox multi-select over a `ValueSource`'s candidates. The checkbox list is fully
 * driven by `source.useCandidates(search)` — this component holds no candidate state of its own,
 * only the local search text. */
export function ConditionValuePicker({source, selectedIds, onChange, operatorPicker}: ConditionValuePickerProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [search, setSearch] = useState('');
	const {items, loading, error, retry} = source.useCandidates(search);

	// Everything already picked stays on the list, whatever the search says.
	//
	// A candidate list is a search result, so a selected value drops off it as soon as the search
	// stops matching. On a picker whose candidates are typed rather than fetched -- a batch id --
	// that happens on the very next keystroke: type a second id and the first becomes a ticked value
	// with no checkbox to untick. The only way back was to delete the whole condition. The same is
	// true of every fetched source, just less often.
	//
	// Selected entries lead, in selection order, so the list does not reorder under the reader as
	// they type. Labels come from `resolveLabels`, which is what the tile summary already renders
	// them with, so a value reads the same in both places.
	const selectedLabels = source.resolveLabels(selectedIds);
	const shown = [
		...selectedIds.map(id => ({id, label: selectedLabels[id] ?? id})),
		...items.filter(item => !selectedIds.includes(item.id)),
	];

	const toggle = (id: string, checked: boolean): void => {
		if (source.singleSelect) {
			onChange(checked ? [id] : []);
			return;
		}
		onChange(checked ? [...selectedIds, id] : selectedIds.filter(x => x !== id));
	};

	// Select all acts on `items`, not on `shown`: `items` is what matches the search, which is what
	// this control promises to select and clear. `shown` additionally carries already-selected values
	// the search no longer matches, kept on screen so they can still be unticked - clearing those from
	// here would throw away selections the reader never asked this control to touch.
	//
	// The checkbox still reads honestly against the rendered list. When every `item` is selected,
	// every row in `shown` is ticked as well, because the extras are selected by definition - so
	// "Select all is ticked" continues to mean "everything on screen is ticked".
	const allFilteredSelected = items.length > 0 && items.every(item => selectedIds.includes(item.id));

	const toggleAll = (): void => {
		const filteredIds = items.map(item => item.id);
		if (allFilteredSelected) {
			// Clears only what the search matches. A value selected but filtered out is not
			// something the reader can see to re-tick, so removing it would be a silent edit.
			onChange(selectedIds.filter(id => !filteredIds.includes(id)));
			return;
		}
		onChange([...selectedIds, ...filteredIds.filter(id => !selectedIds.includes(id))]);
	};

	return (
		<PickerShell>
			{operatorPicker ? (
				<>
					<InsetGroup data-cy={'ai-report-condition-operator-group'}>{operatorPicker}</InsetGroup>
					<Divider data-cy={'ai-report-condition-divider'} />
				</>
			) : null}
			<InsetGroup data-cy={'ai-report-condition-search-group'}>
				<SearchField
					value={search}
					onChange={setSearch}
					isExpandable={false}
					width={'100%'}
					placeholder={formatMessage({id: 'common.search'})}
					data-cy={'ai-report-condition-value-search'}
				/>
			</InsetGroup>
			<Divider data-cy={'ai-report-condition-divider'} />
			{loading ? (
				<InsetGroup>
					<Text color={'medium'}>
						<FormattedMessage id={'ai_report_builder.conditions.value_picker_loading'} />
					</Text>
				</InsetGroup>
			) : error ? (
				<InsetGroupWithGap data-cy={'ai-report-condition-value-error'}>
					<Text color={'medium'}>
						<FormattedMessage id={'ai_report_builder.conditions.value_picker_error'} />
					</Text>
					<Button emphasis={'low'} onPress={() => retry?.()} data-cy={'ai-report-condition-value-retry'}>
						<FormattedMessage id={'common.retry'} />
					</Button>
				</InsetGroupWithGap>
			) : shown.length === 0 ? (
				<InsetGroup>
					<Text color={'medium'}>
						<FormattedMessage id={'ai_report_builder.conditions.value_picker_empty'} />
					</Text>
				</InsetGroup>
			) : (
				<RowList>
					{/* Hidden when nothing matches the search, even though `shown` still lists the
					 * already-selected rows. Select all acts on the candidates, so with none left it has
					 * nothing to select or clear: it would render unticked above a list where every row is
					 * ticked, and clicking it would do nothing at all. */}
					{source.singleSelect || items.length === 0 ? null : (
						<>
							<DropdownList.CheckboxItem
								checked={allFilteredSelected}
								onChange={toggleAll}
								data-cy={'ai-report-condition-select-all'}
							>
								{formatMessage({id: 'common.select_all'})}
							</DropdownList.CheckboxItem>
							<SectionDivider data-cy={'ai-report-condition-divider'} />
						</>
					)}
					{shown.map(item => (
						<DropdownList.CheckboxItem
							key={item.id}
							checked={selectedIds.includes(item.id)}
							onChange={() => toggle(item.id, !selectedIds.includes(item.id))}
							data-cy={`ai-report-condition-value-${item.id}`}
						>
							{item.label}
						</DropdownList.CheckboxItem>
					))}
				</RowList>
			)}
		</PickerShell>
	);
}
